import { useState } from 'react';
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import type { DashboardNavGroup, DashboardNavGroupKey } from '../navigation';
import type { DashboardTranslations } from '../translations';

interface SidebarProps {
  groups: DashboardNavGroup[];
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  t: DashboardTranslations;
}

export function Sidebar({ groups, expanded, onExpandedChange, t }: SidebarProps) {
  const [openGroups, setOpenGroups] = useState<Set<DashboardNavGroupKey>>(
    () => new Set(groups.map((g) => g.key)),
  );

  const toggleGroup = (key: DashboardNavGroupKey) => {
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <LazyMotion features={domAnimation} strict>
      <m.aside
        layout
        className="sticky top-16 hidden h-[calc(100vh-64px)] shrink-0 border-e border-surface-border bg-surface-card/60 backdrop-blur-2xl px-3 py-4 lg:flex lg:flex-col overflow-hidden"
        animate={{ width: expanded ? 280 : 72 }}
        transition={{ type: 'spring', stiffness: 400, damping: 35 }}
      >
        <div className="flex justify-end px-1">
          <button
            type="button"
            onClick={() => onExpandedChange(!expanded)}
            className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-surface-border text-brand-muted hover:border-accent/30 hover:text-brand-white transition-colors"
            aria-label={expanded ? t.sidebar.collapse : t.sidebar.expand}
          >
            {expanded ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
          </button>
        </div>

        <nav className="mt-6 flex-1 overflow-y-auto no-scrollbar" aria-label={t.sidebar.label}>
          <AnimatePresence mode="wait" initial={false}>
            {expanded ? (
              <m.div
                key="expanded"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="space-y-1"
              >
                {groups.map((group) => {
                  const GroupIcon = group.icon;
                  const isOpen = openGroups.has(group.key);
                  return (
                    <div key={group.key}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.key)}
                        className="flex min-h-10 w-full items-center gap-3 rounded-lg px-3 text-xs font-semibold uppercase tracking-widest text-brand-muted hover:text-accent transition-colors"
                        aria-expanded={isOpen}
                      >
                        <GroupIcon size={14} />
                        <span className="flex-1 text-start">{t.nav.groups[group.key]}</span>
                        <m.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                          <ChevronDown size={12} />
                        </m.span>
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen ? (
                          <m.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-1 space-y-px pb-2">
                              {group.items.map((item) => {
                                const Icon = item.icon;
                                return (
                                  <NavLink
                                    key={item.key}
                                    to={item.to}
                                    end={item.to === '/dashboard'}
                                    className={({ isActive }) =>
                                      `flex min-h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors ${
                                        isActive
                                          ? 'bg-surface-card border border-surface-border text-brand-white'
                                          : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'
                                      }`
                                    }
                                  >
                                    <Icon size={16} />
                                    <span className="truncate">{t.nav.items[item.key]}</span>
                                  </NavLink>
                                );
                              })}
                            </div>
                          </m.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </m.div>
            ) : (
              <m.div
                key="collapsed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="space-y-1"
              >
                {groups.flatMap((group) =>
                  group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.key}
                        to={item.to}
                        end={item.to === '/dashboard'}
                        title={t.nav.items[item.key]}
                        className={({ isActive }) =>
                          `flex size-11 items-center justify-center rounded-lg transition-colors ${
                            isActive
                              ? 'bg-accent text-surface-base'
                              : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'
                          }`
                        }
                      >
                        <Icon size={18} />
                      </NavLink>
                    );
                  }),
                )}
              </m.div>
            )}
          </AnimatePresence>
        </nav>
      </m.aside>
    </LazyMotion>
  );
}
